import { useState } from 'react';
import { CreditCard, Plus } from 'lucide-react';
import { KycStatus } from '@shared/types';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { EmptyState, InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { CardForm } from '@/components/cards/CardForm';
import { BankCardItem } from '@/components/cards/BankCardItem';
import { CardControls } from '@/components/cards/CardControls';

import { useKyc } from '@/lib/queries';
import { useRemoveCard } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import type { TraderCard } from '@/types/api';

/**
 * Payment details, and nothing else.
 *
 * The card list used to be the third block of the verification screen, below a
 * stepper and two document uploaders. It is the one part of that screen a
 * trader returns to — a card gets blocked, a new one is issued, the old one
 * expires — while the documents are a once-ever task. So it earns its own tab
 * and the documents keep theirs at `/verification`, reached from the overview
 * only while the account is still unfinished.
 */
export default function CardsPage() {
  const toast = useToast();
  const kyc = useKyc();
  const removeCard = useRemoveCard();
  const [isAddingCard, setIsAddingCard] = useState(false);

  if (kyc.isPending) return <CardsSkeleton />;

  if (kyc.isError) {
    return (
      <div>
        <PageHeading />
        <InlineError
          className="mt-6"
          message={toMessage(kyc.error, 'Не удалось загрузить список карт.')}
          onRetry={() => void kyc.refetch()}
        />
      </div>
    );
  }

  const snapshot = kyc.data;
  // Widening, not casting: every field the money layer added to a card is
  // optional, so the verification snapshot's cards already satisfy this shape.
  const cards: TraderCard[] = snapshot.cards;
  // While the application sits with an administrator the cards are part of it;
  // removing one underneath the review would leave the decision meaningless.
  const isUnderReview = snapshot.status === KycStatus.Pending;

  const handleRemoveCard = async (cardId: string): Promise<void> => {
    try {
      await removeCard.mutateAsync(cardId);
      toast.success('Карта удалена');
    } catch (error) {
      toast.error('Не удалось удалить карту', toMessage(error));
    }
  };

  return (
    <div>
      <PageHeading />

      <Panel padding="none" className="mt-8">
        <PanelHeader
          title="Карты для приёма рублей"
          description={
            isUnderReview
              ? 'Пока заявка на проверке, карты удалить нельзя — они рассматриваются вместе с документами. Добавить новую можно в любой момент.'
              : 'Карты должны быть оформлены на ваше имя и совпадать с загруженным паспортом. Каждая проверяется отдельно.'
          }
          actions={
            !isAddingCard ? (
              <Button
                size="sm"
                icon={<Plus size={13} strokeWidth={1.5} aria-hidden="true" />}
                onClick={() => setIsAddingCard(true)}
              >
                Добавить карту
              </Button>
            ) : undefined
          }
        />

        <div className="px-4 py-4">
          {cards.length === 0 && !isAddingCard ? (
            <EmptyState
              icon={CreditCard}
              title="Карты пока не добавлены"
              description="Добавьте хотя бы одну карту. Мерчанты отправляют рубли прямо на неё, поэтому номер должен быть точным."
              action={
                <Button
                  variant="primary"
                  icon={<Plus size={14} strokeWidth={1.5} aria-hidden="true" />}
                  onClick={() => setIsAddingCard(true)}
                >
                  Добавить первую карту
                </Button>
              }
              className="px-0 py-4"
            />
          ) : null}

          {cards.length > 0 ? (
            // Two columns at most, and only from lg: each row is now a plate
            // plus its limits, usage and switch, and three of those side by side
            // put the meters below a readable width.
            <ul className="grid gap-6 lg:grid-cols-2">
              {cards.map((card) => (
                <li key={card.id} className="min-w-0">
                  <BankCardItem
                    card={card}
                    isRemoving={removeCard.isPending}
                    onRemove={isUnderReview ? undefined : (cardId) => void handleRemoveCard(cardId)}
                  />
                  <CardControls card={card} />
                </li>
              ))}
            </ul>
          ) : null}

          {isAddingCard ? (
            <div className="mt-5 border-t border-line pt-5">
              <h3 className="eyebrow mb-3">Новая карта</h3>
              <div className="max-w-prose">
                <CardForm onDone={() => setIsAddingCard(false)} />
              </div>
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

function PageHeading() {
  return (
    <header>
      <h1 className="text-xl font-semibold tracking-tight text-fg">Реквизиты</h1>
      <p className="mt-1.5 max-w-prose text-sm text-fg-secondary">
        Банковские карты, на которые мерчанты отправляют рубли, и лимиты по каждой из них.
      </p>
    </header>
  );
}

function CardsSkeleton() {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">Загружаю карты</span>
      <Skeleton className="h-7 w-36" />
      <Skeleton className="mt-3 h-3.5 w-72" />
      <div className="mt-8 rounded-md border border-line bg-surface">
        <div className="border-b border-line px-4 py-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="mt-2 h-3 w-full max-w-prose" />
        </div>
        <div className="grid gap-3 px-4 py-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} className="aspect-[1.6/1] max-w-[20rem] rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}
